// Composition statement expansion for selector + class-variable chains.
// Example: ".btn &var(--hoverRule);" -> ".btn:hover { ... }".
import { combineSelector } from '../parser'
import { startsDeclarationLike } from './declarations'
import { resolveClassVariableRule } from './replacements'
import { parseVarReferenceAt, consumeAmpersandPrefix } from './scanner'
import { appendSelectorText, appendTokenSelector, resolveSelectorReferences } from './selector'
import type { ClassTokenMatch, ClassTransformOptions } from './types'

// Expand one composition statement into concrete selector rule if valid.
export function transformCompositionStatement(statement: string, options: ClassTransformOptions): string | null {
  const trimmed = statement.trim()
  // Reject empty and declaration-like statements from composition path.
  if (!trimmed || startsDeclarationLike(trimmed)) {
    return null
  } else {
    // Continue class composition token scanning.
  }

  const classTokens = collectClassCompositionTokens(trimmed, options.classVariables)
  // Return null when statement does not include class-variable tokens.
  if (classTokens.length === 0) {
    return null
  } else {
    // Continue selector/body reconstruction from token list.
  }

  let selector = ''
  let body = ''
  let cursor = 0

  // Build final selector by walking text fragments and class tokens in order.
  for (let tokenIndex = 0; tokenIndex < classTokens.length; tokenIndex += 1) {
    const token = classTokens[tokenIndex]
    const beforeSource = resolveSelectorReferences(trimmed.slice(cursor, token.start), options)
    selector = appendSelectorText(selector, beforeSource)

    const tokenSelector = resolveSelectorReferences(token.rule.selector, options).trim()
    // Reject token when resolved selector part is empty.
    if (!tokenSelector) {
      return null
    } else {
      // Continue selector combination for resolved token.
    }

    // Apply ampersand composition against current selector.
    if (token.hasAmpersandPrefix) {
      const baseSelector = selector.trim()
      // Reject invalid `&var(...)` usage without a base selector.
      if (!baseSelector) {
        return null
      } else {
        // Apply `&`-based selector composition.
        selector = combineSelector(baseSelector, tokenSelector)
      }
      // Apply direct class token with whitespace-sensitive merge behavior.
    } else {
      const previousChar = token.start > 0 ? trimmed[token.start - 1] : ''
      selector = appendTokenSelector(selector, tokenSelector, previousChar)
    }

    body = token.rule.body.trim()
    cursor = token.end
  }

  const trailingSource = resolveSelectorReferences(trimmed.slice(cursor), options)
  selector = appendSelectorText(selector, trailingSource)
  const finalSelector = selector.trim()
  // Reject invalid composition when selector or body are empty.
  if (!finalSelector || !body) {
    return null
  } else {
    // Return finalized selector rule text.
    return `${finalSelector} { ${body} }`
  }
}

// Collect class-variable token matches from one statement.
function collectClassCompositionTokens(
  statement: string,
  classVariables: Record<string, string>,
): ClassTokenMatch[] {
  const tokens: ClassTokenMatch[] = []
  let index = 0

  // Scan statement once and parse `var(--name)` tokens with optional `&`.
  while (index < statement.length) {
    const ampCandidate = consumeAmpersandPrefix(statement, index)
    const varStart = ampCandidate ? ampCandidate.nextIndex : index
    const reference = parseVarReferenceAt(statement, varStart)
    // Advance one character when no var-reference starts at this index.
    if (!reference || reference.objectKey || reference.arrayIndex !== null) {
      index += 1
      continue
    } else {
      // Continue class token resolution for parsed var token.
    }

    const classValue = classVariables[reference.variableName]
    // Keep scanning when variable is not a class-style rule variable.
    if (!classValue) {
      index = reference.end
      continue
    } else {
      // Continue class rule parsing for candidate variable.
    }

    const classRule = resolveClassVariableRule(reference.variableName, {
      classVariables,
      variables: {},
      objectVariables: {},
      arrayVariables: {},
    })
    // Keep scanning when class variable content is not parseable.
    if (!classRule) {
      index = reference.end
      continue
    } else {
      // Push resolved class composition token.
    }

    const start = ampCandidate ? ampCandidate.startIndex : reference.start
    tokens.push({
      start,
      end: reference.end,
      hasAmpersandPrefix: Boolean(ampCandidate),
      rule: classRule,
    })
    index = reference.end
  }

  // Return collected class token matches.
  return tokens
}
